import React, { useState, useEffect } from 'react';
import { Check, Plus, Zap, Target, Gift, Clock, BookOpen, Sun, Moon, Terminal, RotateCw, Flame, Diamond, Heart, Shield } from 'lucide-react';

function RightSidebar({ hearts, streak, darkMode, gems, lastHeartUpdate, onRefillHearts, onStartPractice, onShowMissions, onShowAbout, onShowImpressum, onShowPrivacy, onShowCourses, activeCourse = 'Python', completedLessons = [], onSelectCourse, dailyMissions = [], onShowAdModal, freezeUsedDates = [] }) {
  const [showStreakPopup, setShowStreakPopup] = useState(false);
  const [showCoursePopup, setShowCoursePopup] = useState(false);
  const [showHeartsPopup, setShowHeartsPopup] = useState(false);
  const [timeUntilNextHeart, setTimeUntilNextHeart] = useState('');
  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const todayIndex = (new Date().getDay() + 6) % 7; // 0=Mo

  const getCourseIcon = (courseName) => {
    if (courseName === 'JavaScript') return "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg";
    if (courseName === 'HTML') return "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg";
    if (courseName === 'Kotlin') return "https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg";
    return "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg";
  };

  const currentIcon = getCourseIcon(activeCourse);

  const allCourses = [
    { name: 'HTML', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg" },
    { name: 'Python', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg" },
    { name: 'JavaScript', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg" },
    { name: 'Kotlin', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg" },
    { name: 'Java', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/java/java-original.svg" },
    { name: 'C++', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/cplusplus/cplusplus-original.svg" },
    { name: 'C#', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/csharp/csharp-original.svg" },
    { name: 'SQL', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/mysql/mysql-original.svg" },
    { name: 'React', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg" },
    { name: 'TypeScript', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg" },
    { name: 'Swift', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/swift/swift-original.svg" },
    { name: 'Go', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/go/go-original.svg" },
    { name: 'Rust', icon: "https://raw.githubusercontent.com/devicons/devicon/master/icons/rust/rust-original.svg" },
  ];

  const myCourses = allCourses.filter(c => c.name === activeCourse || completedLessons.includes(c.name + '_1'));

  useEffect(() => {
    if (hearts < 5 && lastHeartUpdate) {
      const updateTimer = () => {
        const now = Date.now();
        const msPerHeart = 5 * 60 * 60 * 1000; // 5 Stunden
        const nextHeartTime = lastHeartUpdate + msPerHeart;
        const diff = nextHeartTime - now;

        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeUntilNextHeart(`${hours}h ${minutes}m`);
        } else {
          setTimeUntilNextHeart('0h 0m');
        }
      };
      updateTimer();
      const interval = setInterval(updateTimer, 60000); // Jede Minute aktualisieren
      return () => clearInterval(interval);
    }
  }, [hearts, lastHeartUpdate]);

  return (
    <aside className="right-sidebar" style={{
      backgroundColor: darkMode ? '#131313' : 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      overflow: 'visible'
    }}>
      <div className="stats-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
        <div 
          className="stat-item course-selector"
          onMouseEnter={() => setShowCoursePopup(true)}
          onMouseLeave={() => setShowCoursePopup(false)}
          style={{ 
            position: 'relative', 
            cursor: 'pointer',
            backgroundColor: showCoursePopup ? (darkMode ? '#333' : '#f0f0f0') : 'transparent',
            borderRadius: '12px',
            transition: 'background-color 0.2s',
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            alignSelf: 'stretch'
          }}
        >
          <img src={currentIcon} alt={activeCourse} width="28" />
          
          {showCoursePopup && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '-10px',
              paddingTop: '10px',
              zIndex: 1000,
              width: '280px',
              cursor: 'default',
              textAlign: 'left'
            }}>
              <div style={{
                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
                borderRadius: '16px',
                padding: '15px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 'bold', 
                  color: darkMode ? '#aaa' : '#afafaf', 
                  marginBottom: '15px',
                  paddingLeft: '10px'
                }}>
                  MEINE KURSE
                </div>
                
                {myCourses.map(course => (
                  <div
                    key={course.name}
                    onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onSelectCourse && onSelectCourse(course.name); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '10px',
                      borderRadius: '12px',
                      backgroundColor: activeCourse === course.name ? (darkMode ? '#333' : '#ddf4ff') : 'transparent',
                      border: activeCourse === course.name ? `2px solid ${darkMode ? '#333' : '#1cb0f6'}` : '2px solid transparent',
                      cursor: 'pointer',
                      marginBottom: '5px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={e => { if(activeCourse !== course.name) e.currentTarget.style.backgroundColor = darkMode ? '#333' : '#f7f7f7'; }}
                    onMouseLeave={e => { if(activeCourse !== course.name) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <img src={course.icon} alt={course.name} width="32" />
                    <span style={{ fontWeight: 'bold', fontSize: '1rem', color: activeCourse === course.name ? (darkMode ? '#fff' : '#1cb0f6') : (darkMode ? '#ccc' : '#4b4b4b') }}>{course.name}</span>
                  </div>
                ))}

                <div 
                  className="add-course-button"
                  onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowCourses(); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    padding: '10px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    color: darkMode ? '#aaa' : '#777',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '8px', 
                    border: '2px dashed #ccc', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#ccc'
                  }}>
                    <Plus size={20} />
                  </div>
                  <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>Neuen Kurs hinzufügen</span>
                </div>
                <style>{`
                  .add-course-button:hover {
                    background-color: ${darkMode ? '#333' : '#f7f7f7'};
                  }
                `}</style>
              </div>
            </div>
          )}
        </div>
        <div 
          className="stat-item streak"
          onMouseEnter={() => setShowStreakPopup(true)}
          onMouseLeave={() => setShowStreakPopup(false)}
          style={{ 
            position: 'relative', 
            cursor: 'pointer',
            backgroundColor: showStreakPopup ? (darkMode ? '#333' : '#f0f0f0') : 'transparent',
            borderRadius: '12px',
            transition: 'background-color 0.2s',
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            alignSelf: 'stretch'
          }}
        >
          <Flame size={28} color="#ff9600" />
          <span style={{ fontWeight: '800' }}>{streak}</span>
          {showStreakPopup && (
            <div style={{
              position: 'absolute',
              top: '120%',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: darkMode ? '#2b2b2b' : '#fff',
              border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
              borderRadius: '16px',
              padding: '0',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              zIndex: 1000,
              width: '300px',
              cursor: 'default',
              overflow: 'hidden'
            }}>
              <div style={{
                backgroundColor: '#ff9600',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '5px'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase' }}>Streak</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '48px' }}>🔥</span>
                    <span style={{ fontSize: '3rem', fontWeight: '800', color: 'white' }}>{streak}</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px' }}>
                {weekDays.map((day, index) => {
                  const d = new Date();
                  d.setDate(d.getDate() + (index - todayIndex));
                  const dString = d.toDateString();
                  
                  const isFrozen = freezeUsedDates && freezeUsedDates.includes(dString);
                  const inStreakRange = index <= todayIndex && (todayIndex - index) < streak;
                  const isActive = inStreakRange && !isFrozen;

                  let bgColor = 'transparent';
                  let borderColor = darkMode ? '#444' : '#e5e5e5';
                  let textColor = darkMode ? '#888' : '#aaa';

                  if (isFrozen && inStreakRange) {
                    bgColor = '#1cb0f6';
                    borderColor = '#1cb0f6';
                    textColor = '#1cb0f6';
                  } else if (isActive) {
                    bgColor = '#ff9600';
                    borderColor = '#ff9600';
                    textColor = '#ff9600';
                  }

                  return (
                    <div key={day} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: textColor }}>{day}</div>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: `2px solid ${borderColor}`,
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        {isActive && <Check size={16} strokeWidth={4} />}
                        {isFrozen && inStreakRange && <Shield size={16} strokeWidth={3} fill="currentColor" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="stat-item gems">
          <Diamond size={28} color="#1cb0f6" />
          <span style={{ fontWeight: '800' }}>{gems}</span>
        </div>
        <div 
          className="stat-item hearts"
          onMouseEnter={() => setShowHeartsPopup(true)}
          onMouseLeave={() => setShowHeartsPopup(false)}
          style={{ 
            position: 'relative', 
            cursor: 'pointer',
            backgroundColor: showHeartsPopup ? (darkMode ? '#333' : '#f0f0f0') : 'transparent',
            borderRadius: '12px',
            transition: 'background-color 0.2s',
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            alignSelf: 'stretch'
          }}
        >
          <Heart size={28} color="#ff4b4b" />
          <span style={{ fontWeight: '800' }}>{hearts}</span>
          {showHeartsPopup && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              paddingTop: '10px',
              zIndex: 1000,
              width: '360px',
              cursor: 'default'
            }}>
              <div style={{
                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
                borderRadius: '16px',
                padding: '0',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                textAlign: 'center',
                overflow: 'hidden'
              }}>
                <div style={{
                  backgroundColor: '#ff4b4b',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase' }}>Herzen</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '48px' }}>❤️</span>
                      <span style={{ fontSize: '3rem', fontWeight: '800', color: 'white' }}>{hearts}</span>
                  </div>
                </div>
                <div style={{ padding: '20px' }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: '20px', color: darkMode ? '#ccc' : '#777', fontWeight: '500' }}>
                    {hearts === 5 ? 'Du hast volle Gesundheit!' : (
                      <>
                        Nächstes Herz in: <span style={{ color: '#ff4b4b' }}>{timeUntilNextHeart}</span>
                      </>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button 
                      className="popup-button"
                      disabled={hearts === 5}
                      onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onStartPractice(); }}
                      style={{ 
                        width: '100%', 
                        padding: '10px 16px', 
                        borderRadius: '12px', 
                        border: `2px solid ${darkMode ? '#444' : '#e5e5e5'}`, 
                        backgroundColor: darkMode ? 'transparent' : '#fff', 
                        color: hearts === 5 ? (darkMode ? '#555' : '#afafaf') : (darkMode ? '#ccc' : '#777'), 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem', 
                        cursor: hearts === 5 ? 'default' : 'pointer', 
                        boxShadow: hearts === 5 ? 'none' : `0 4px 0 ${darkMode ? '#444' : '#e5e5e5'}`, 
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        opacity: hearts === 5 ? 0.5 : 1
                      }}
                    >
                      <Heart size={24} color={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} fill={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} />
                      <span>ÜBEN UND HERZEN VERDIENEN</span>
                    </button>
                    <button 
                      className="popup-button"
                      disabled={hearts === 5}
                      onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowAdModal(); }}
                      style={{ 
                        width: '100%', 
                        padding: '10px 16px', 
                        borderRadius: '12px', 
                        border: `2px solid ${darkMode ? '#444' : '#e5e5e5'}`, 
                        backgroundColor: darkMode ? 'transparent' : '#fff', 
                        color: hearts === 5 ? (darkMode ? '#555' : '#afafaf') : (darkMode ? '#ccc' : '#777'), 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem', 
                        cursor: hearts === 5 ? 'default' : 'pointer', 
                        boxShadow: hearts === 5 ? 'none' : `0 4px 0 ${darkMode ? '#444' : '#e5e5e5'}`, 
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        opacity: hearts === 5 ? 0.5 : 1
                      }}
                    >
                      <Heart size={24} color={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} fill={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} />
                      <span>WERBUNG SCHAUEN</span>
                    </button>
                    <button 
                      className="popup-button"
                      onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onRefillHearts(); }}
                      disabled={hearts === 5}
                      style={{ width: '100%', padding: '10px 16px', borderRadius: '12px', border: `2px solid ${darkMode ? '#444' : '#e5e5e5'}`, backgroundColor: darkMode ? 'transparent' : '#fff', color: hearts === 5 ? (darkMode ? '#555' : '#afafaf') : (darkMode ? '#fff' : '#4b4b4b'), fontWeight: 'bold', fontSize: '0.9rem', cursor: hearts === 5 ? 'default' : 'pointer', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: hearts === 5 ? 0.5 : 1, boxShadow: hearts === 5 ? 'none' : `0 4px 0 ${darkMode ? '#444' : '#e5e5e5'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Heart size={24} color={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} fill={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#ff4b4b'} />
                        <span>Herzen auffüllen</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Diamond size={20} color={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#1cb0f6'} fill={hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#1cb0f6'} />
                        <span style={{ color: hearts === 5 ? (darkMode ? '#555' : '#afafaf') : '#1cb0f6' }}>350</span>
                      </div>
                    </button>
                  </div>
                  <style>{`
                    .popup-button {
                      transition: transform 0.1s, box-shadow 0.1s;
                    }
                    .popup-button:active:not(:disabled) {
                      transform: translateY(4px);
                      box-shadow: none !important;
                    }
                  `}</style>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="widget-card quests-widget" style={{ 
        width: 'calc(100% + 20px)', 
        boxSizing: 'border-box', 
        margin: '0 -20px 0 0', 
        maxWidth: 'none',
        border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
        borderRadius: '16px',
        padding: '20px',
        backgroundColor: darkMode ? '#131313' : '#fff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: darkMode ? '#fff' : '#4b4b4b' }}>Tagesmissionen</h3>
            <span onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowMissions(); }} style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1cb0f6', cursor: 'pointer', textTransform: 'uppercase' }}>Alle anzeigen</span>
        </div>
        
        {dailyMissions.slice(0, 3).map((mission, index) => {
          const Icon = {
            Zap: Zap,
            Clock: Clock,
            BookOpen: BookOpen,
            Target: Target,
            Sun: Sun,
            Moon: Moon,
            Terminal: Terminal,
            RotateCw: RotateCw
          }[mission.icon] || Zap;

          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: index < 2 ? '20px' : '0' }}>
                <div style={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                    <Icon size={32} color={mission.completed ? '#58cc02' : (mission.icon === 'Zap' ? '#ffc800' : '#1cb0f6')} fill={mission.icon === 'Zap' && !mission.completed ? '#ffc800' : 'none'} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: darkMode ? '#e5e5e5' : '#4b4b4b', marginBottom: '8px' }}>{mission.description}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1, height: '16px', backgroundColor: darkMode ? '#333' : '#e5e5e5', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                            <div style={{ width: `${Math.min(100, (mission.progress / mission.target) * 100)}%`, height: '100%', backgroundColor: mission.completed ? '#58cc02' : '#ffc800', position: 'absolute', top: 0, left: 0 }}></div>
                            <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700', color: mission.completed ? '#fff' : (darkMode ? '#fff' : '#4b4b4b'), zIndex: 1 }}>{mission.progress} / {mission.target}</div>
                        </div>
                        {mission.completed ? (
                          <Check size={24} color="#58cc02" strokeWidth={4} />
                        ) : (
                          <Gift size={24} color={darkMode ? '#555' : '#afafaf'} />
                        )}
                    </div>
                </div>
            </div>
          );
        })}
      </div>

      <div style={{ 
        marginTop: '30px', 
        textAlign: 'center', 
        color: darkMode ? '#777' : '#afafaf', 
        fontSize: '0.75rem', 
        fontWeight: 'bold',
        padding: '0 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <span style={{ cursor: 'pointer' }} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowAbout(); }}>ÜBER UNS</span>
          <span style={{ cursor: 'pointer' }} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); }}>BLOG</span>
          <span style={{ cursor: 'pointer' }} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); }}>KONTAKT</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <span style={{ cursor: 'pointer' }} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowPrivacy(); }}>DATENSCHUTZ</span>
          <span style={{ cursor: 'pointer' }} onClick={() => { if (navigator.vibrate) navigator.vibrate(10); onShowImpressum(); }}>IMPRESSUM</span>
        </div>
      </div>
    </aside>
  );
}

export default RightSidebar;